/**
 * Waits for Cobrowse `currentSession.id` after `createSessionCode()`.
 * The SDK often populates `currentSession` asynchronously; reading `id()` immediately
 * leaves `cobrowseSessionId` blank so the rep iframe falls back to `/code/...`.
 */

export interface CobrowseSessionMinimal {
  code: () => string;
  id: () => string;
}

export interface CobrowseSdkMinimal {
  currentSession: CobrowseSessionMinimal | null;
  on: (event: string, callback: (session: CobrowseSessionMinimal) => void) => void;
  off: (event: string, callback: (session: CobrowseSessionMinimal) => void) => void;
}

export const COBROWSE_SESSION_ID_RESOLVE_MS = 5000;

export async function resolveCobrowseSessionIdAfterCode(
  sdk: CobrowseSdkMinimal,
  timeoutMs: number,
): Promise<string> {
  function readCurrentId(): string {
    try {
      const id = sdk.currentSession?.id?.();
      return typeof id === 'string' ? id : '';
    } catch {
      return '';
    }
  }

  const immediate = readCurrentId();
  if (immediate) {
    return immediate;
  }

  return new Promise((resolve) => {
    let settled = false;
    const pollIntervalMs = 50;
    let pollHandle: ReturnType<typeof setInterval> | null = null;

    function finish(id: string): void {
      if (settled) {
        return;
      }
      settled = true;
      sdk.off('session.started', onStarted);
      if (pollHandle) {
        clearInterval(pollHandle);
      }
      resolve(id);
    }

    function onStarted(session: CobrowseSessionMinimal): void {
      try {
        const sessionId = session.id?.();
        if (sessionId) {
          finish(sessionId);
        }
      } catch {
        // ignore
      }
    }

    sdk.on('session.started', onStarted);

    const deadline = Date.now() + timeoutMs;
    pollHandle = setInterval(() => {
      const id = readCurrentId();
      if (id) {
        finish(id);
        return;
      }
      if (Date.now() >= deadline) {
        finish('');
      }
    }, pollIntervalMs);
  });
}
